import jwtDecode from 'jwt-decode';
import dayjs from 'dayjs';
import uuidParse from 'uuid-parse';
import {Buffer} from 'buffer';
import {callAPI} from '@/store/utils';
import {isPWA} from '@/utils/isPWA';
import en from '@/data/tracking';

// in every place where we would like set locale - dayjs.locale(...) all locales should be imported
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';

import {
  getLocaleWithWeglot,
  isUserLogged,
  waitForWeglot,
} from '@/utils/user.js';

const FYGR_ACCESS_TOKEN = 'FYGR_ACCESS_TOKEN';
const FYGR_COMPANY = 'FYGR_COMPANY';
const FYGR_PRETENDED_USER = 'FYGR_PRETENDED_USER';
const FYGR_USER_FINISHING_SIGNUP = 'FYGR_USER_FINISHING_SIGNUP';
const FYGR_USER_SELECTED_PARTNER = 'FYGR_USER_SELECTED_PARTNER';
const FYGR_CURRENT_SELECTED_PARTNER = 'FYGR_CURRENT_SELECTED_PARTNER';

const FYGR_POST_LOGIN_REDIRECT = 'FYGR_POST_LOGIN_REDIRECT';

const REDIRECT_TO_BLOCKED_PREFIXES = [
  '/signin',
  '/signup',
  '/signup-en',
  '/signup-cpa',
  '/signup-cpa-rca',
  '/passwordrecovery',
  '/completeSignUp',
  '/callback',
];

export function isSafeRedirectTarget(path) {
  if (typeof path !== 'string' || path.length === 0) return false;
  // Must be a same-origin absolute path. Reject protocol-relative ("//evil.com"),
  // backslash tricks ("/\\evil.com"), and any scheme.
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  // Don't bounce users back to auth/public flows.
  const pathname = path.split('?')[0].split('#')[0];
  return !REDIRECT_TO_BLOCKED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function stashPostLoginRedirect() {
  if (typeof window === 'undefined') return;
  const current = window.location.pathname + window.location.search;
  if (!isSafeRedirectTarget(current)) return;
  try {
    window.sessionStorage.setItem(FYGR_POST_LOGIN_REDIRECT, current);
  } catch {
    // sessionStorage may be unavailable (private mode, quota); fail silently
  }
}

export function consumePostLoginRedirect() {
  if (typeof window === 'undefined') return null;
  let value = null;
  try {
    value = window.sessionStorage.getItem(FYGR_POST_LOGIN_REDIRECT);
    window.sessionStorage.removeItem(FYGR_POST_LOGIN_REDIRECT);
  } catch {
    return null;
  }
  return isSafeRedirectTarget(value) ? value : null;
}

/* Conserved for future use - hashing of the data has been relocated to GTM */
// function hashData(data) {
//   if (!data) return '';
//   const encoder = new TextEncoder();
//   const dataBuffer = encoder.encode(data);
//   return crypto.subtle.digest('SHA-256', dataBuffer).then((hashBuffer) => {
//     return Array.from(new Uint8Array(hashBuffer))
//       .map((byte) => byte.toString(16).padStart(2, '0'))
//       .join('');
//   });
// }

export default {
  namespaced: true,
  state: {
    pwaMode: isPWA(),
    isAdBlockerDetected: false,
    user: null,
    owner: true,
    isCompanyCpa: false,
    companiesLoaded: false,
    authorized: false,
    notifications: [],
    permissions: {},
    companyInfos: {},
    groups: [],
    pretendedUser: null,
    pretendedCompany: null,
    params: null,
    selectedCompany: null,
    selectedCompanyAllData: null,
    profileData: null,
    finishingSignup: false,
    salesVisibility: false,
    forecastEnabled: false,
    isStandalone: false,
    defaultCurrency: 'EUR',
    maxConnections: 3,
    maxCategoryDepth: 4,
    availableDepthPlans: 1,
    companies: {},
    isActiveForCurrentCompany: false,
    invoicesAllowed: false,
    analyticsAllowed: false,
    incurredProgressBarsEnabled: true,
    maxEngagedObjectiveEnabled: true,
    checksAllowed: false,
    isCompanyEmployee: false,
    signinPopup: false,
    bankConnections: 0,
    forecastVersion: '',
    finishingSignupUserData: {},
    currentStep: 1,
    showStepperLastStep: false,
    gsheet: null,
    fromLocation: null,
    preferences: null,
    employeeList: [],
  },
  mutations: {
    setEmployeeList(state, employeeList) {
      state.employeeList = employeeList?.users || [];
    },
    setAdBlockerDetected(state, status) {
      state.isAdBlockerDetected = status;
    },
    unauthUser(state) {
      state.user = null;
      state.owner = true;
      state.signinPopup = false;
      state.authorized = false;
      state.notifications = [];
      state.permissions = {};
      state.groups = [];
      state.pretendedUser = null;
      state.pretendedCompany = null;
      state.params = null;
      state.selectedCompany = null;
      state.selectedCompanyAllData = null;
      state.profileData = null;
      state.forecastEnabled = false;
      state.forecastVersion = 'v1';
      state.salesVisibility = false;
      state.isStandalone = false;
      state.defaultCurrency = 'EUR';
      state.maxConnections = 3;
      state.maxCategoryDepth = 4;
      state.availableDepthPlans = 1;
      state.companies = {};
      state.isActiveForCurrentCompany = true;
      state.invoicesAllowed = false;
      state.analyticsAllowed = false;
      state.incurredProgressBarsEnabled = true;
      state.maxEngagedObjectiveEnabled = true;
      state.checksAllowed = false;
      state.isCompanyCpa = false;
      state.companiesLoaded = false;
      state.isCompanyEmployee = false;
      state.finishingSignupUserData = {};
      state.currentStep = 1;
      state.showStepperLastStep = false;
      state.gsheet = null;
      state.fromLocation = null;
      state.preferences = null;
    },
    removeFromCompanies(state, id) {
      const newCompanies = {...state.companies};
      delete newCompanies[id];
      state.companies = {...newCompanies};
    },
    setMaxConnections(state, data) {
      state.maxConnections = data?.maxConnections;
    },
    setMaxCategoryDepth(state, data) {
      state.maxCategoryDepth = data.maxDepth;
      state.availableDepthPlans = data.availableDepthPlans;
    },
    setCompanyData(state, data) {
      state.selectedCompany = data.company;
      state.selectedCompanyAllData = data;
    },
    setPermissions(state, permissions) {
      state.permissions = permissions;
    },
    setOwner(state, owner) {
      state.owner = owner;
    },
    setGSheetsLink(state, gsheet) {
      state.gsheet = gsheet;
    },
    setIsStandalone(state, isStandalone) {
      state.isStandalone = isStandalone;
    },
    setSalesVisibility(state, salesVisibility) {
      state.salesVisibility = salesVisibility;
    },
    setIsCompanyEmployee(state, isCompanyEmployee) {
      state.isCompanyEmployee = isCompanyEmployee;
    },
    setIsActiveForCurrentCompany(state, isActive) {
      if (
        state.owner ||
        (state.user && state.user.role && state.user.role === 'SuperAdmin')
      ) {
        state.isActiveForCurrentCompany = true;
        return;
      }
      if (!state.owner) {
        state.isActiveForCurrentCompany = isActive;
      }
    },
    setForecastEnabled(state, forecastEnabled) {
      state.forecastEnabled = forecastEnabled;
    },
    setForecastVersion(state, forecastVersion) {
      state.forecastVersion = forecastVersion;
    },
    setUserPreferences(state, userPreferences) {
      state.preferences = userPreferences;
    },
    setLocale(state, locale) {
      if (!state.preferences) {
        state.preferences = {};
      }
      state.preferences.locale = locale;
    },
    initStorage(state) {
      try {
        state.pretendedUser = JSON.parse(
          localStorage.getItem(FYGR_PRETENDED_USER),
        );
      } catch {
        console.log('No pretended user');
      }
    },
    setUserCurrency(state, currency) {
      state.defaultCurrency = currency;
    },
    setUser(state, user) {
      state.user = state.user ? {...state.user, ...user} : user;
    },
    setSigninPopup(state, status) {
      if (status) {
        localStorage.removeItem(FYGR_ACCESS_TOKEN);
      }
      state.signinPopup = status;
    },
    removeGroup(state, id) {
      const groups = [...state.groups];
      const idx = groups.findIndex((g) => g.id === id);
      groups.splice(idx, 1);
      state.groups = [...groups];
    },
    modifyGroup(state, group) {
      const groups = [...state.groups];
      const idx = groups.findIndex((g) => g.id === group.id);
      if (idx !== -1) {
        groups[idx] = {...groups[idx], ...group};
      } else {
        groups.push(group);
      }
      state.groups = [...groups];
    },
    setGroups(state, groups) {
      state.groups = groups;
    },
    setPretendedUser(state, user) {
      if (user.id === state.user.id) {
        state.pretendedUser = null;
        localStorage.removeItem(FYGR_PRETENDED_USER);
      } else {
        state.pretendedUser = user;
        localStorage.setItem(FYGR_PRETENDED_USER, JSON.stringify(user));
      }
    },
    setAuthorized(state, authorized) {
      state.authorized = authorized;
    },
    setFinishingSignup(state, finishingSignup) {
      state.finishingSignup = finishingSignup;
    },
    addNotification(state, notification) {
      state.notifications.push(notification);
    },
    setBackendParams(state, params) {
      state.params = params;
    },
    setProfileData(state, data) {
      state.profileData = data;

      state.profileData.advancedConversionProperties = {
        user_email: data?.profile?.email
          ? data?.profile?.email.toLowerCase()
          : '',
        user_firstname: data?.profile?.firstName
          ? data?.profile?.firstName.toLowerCase()
          : '',
        user_lastname: data?.profile?.lastName
          ? data?.profile?.lastName.toLowerCase()
          : '',
        user_phone: data?.profile?.telephone?.international_number || '',
      };
    },
    setCompanies(state, data) {
      state.companies = data;
      state.companiesLoaded = true;
    },
    addCompany(state, data) {
      const companies = {...state.companies};
      /* eslint-disable */
      companies[data[0]] = data[1];
      /* eslint-enable */
      state.companies = companies;
    },
    setInvoicesAllowed(state, data) {
      state.invoicesAllowed = data;
    },
    setAnalyticsAllowed(state, data) {
      state.analyticsAllowed = data;
    },
    setDashboardSettings(state, data) {
      state.companyInfos = data;
      state.incurredProgressBarsEnabled = data.incurred_progress_bars_enabled;
      state.maxEngagedObjectiveEnabled = data.max_engaged_objective_enabled;
    },
    setChecksAllowed(state, data) {
      state.checksAllowed = data;
    },
    setBankConnections(state, data) {
      state.bankConnections = data;
    },
    setIsCpa(state, data) {
      state.isCompanyCpa = data;
    },
    setFinishingSignupUserData(state, finishingSignupUserData) {
      state.finishingSignupUserData = finishingSignupUserData;
    },
    setCurrentStep(state, currentStep) {
      state.currentStep = currentStep;
    },
    setStepperLastStep(state, lastStep) {
      state.showStepperLastStep = lastStep;
    },
    setFromLocation(state, fromLocation) {
      state.fromLocation = fromLocation;
    },
  },
  getters: {
    activeUser(state) {
      return state.pretendedUser || state.user || {};
    },
    getLocaleIso(state) {
      return state.preferences && state.preferences.locale
        ? state.preferences.locale
        : 'fr-FR';
    },
    getLocale(state) {
      return state.preferences && state.preferences.locale
        ? state.preferences.locale.substring(0, 2)
        : 'fr';
    },
    isDemoAccount(state) {
      return state.selectedCompany?.is_demo || false;
    },
    isProfileVerified(state) {
      return !!(
        state.profileData?.profile?.is_verified ||
        state.profileData?.profile?.verefied
      );
    },
    userObj(state) {
      return {
        id: state.user?.id,
        isCompanyCpa: state.user?.isCompanyCpa,
        isActive: state.user?.is_active,
        role: state.user?.role,
        // email: state.user?.email,  Would like not to provide emails for safety reasons
      };
    },
    forecastByAccount(state) {
      return (
        state.selectedCompany?.account_level_forecasting_enabled &&
        state.selectedCompany?.account_level_forecasting_activated
      );
    },
    defaultForecastingAccount(state) {
      return state.selectedCompany?.default_forecasting_account_id;
    },
    pretendedUser(state) {
      return state.pretendedUser?.id && state.pretendedUser?.companyId
        ? {
            pretended_user_id: state.pretendedUser?.id,
            company_id: state.pretendedUser?.companyId,
            role: state.pretendedUser?.role,
            pretended_user_email: state.pretendedUser?.email,
            admin_user_id: state.user?.id,
            admin_user_email: state.user?.ex_email,
          }
        : 'Not Pretending';
    },
  },
  actions: {
    async actionUnAuthUser({commit}) {
      commit('setAuthorized', false);
      commit('unauthUser');
      localStorage.removeItem('NOT_INTERESTED_IN_LIBEO');
      localStorage.removeItem(FYGR_PRETENDED_USER);
      localStorage.removeItem(FYGR_ACCESS_TOKEN);
      localStorage.removeItem(FYGR_COMPANY);
      localStorage.removeItem(FYGR_USER_FINISHING_SIGNUP);
      localStorage.removeItem(FYGR_USER_SELECTED_PARTNER);
      localStorage.removeItem(FYGR_CURRENT_SELECTED_PARTNER);
      // TODO: IF issue happens check out Github to restore.
    },
    async actionInitUser({commit, dispatch}) {
      const token = localStorage.getItem(FYGR_ACCESS_TOKEN);
      commit('initStorage');
      if (!token) {
        dispatch('actionUnAuthUser');
        return;
      }
      let companyId = localStorage.getItem(FYGR_COMPANY);
      const pretendUserToken = localStorage.getItem(FYGR_PRETENDED_USER);
      if (!companyId) {
        companyId = null;
      }
      const res = await callAPI('POST', 'getCompanyInfos', {companyId}).catch(
        (err) => {
          // TODO: IF issue happens check out Github to restore.
          dispatch('actionUnAuthUser');
          throw err;
        },
      );
      companyId = res?.result?.company;
      const ice = await callAPI(
        'POST',
        'isCompanyEmployee',
        {companyId},
        true,
      ).catch((err) => {
        dispatch('actionUnAuthUser');
        throw err;
      });
      await dispatch(
        'company/actionGetStyles',
        {
          headerColor: res.result?.app_header_bg_color,
          logo: res.result?.app_logo,
          icon: res.result?.app_icon,
          consentIcon: res.result?.consent_icon,
          contentKey: res.result?.content_key,
          appMenuTextColor: res.result?.app_menu_text_color,
          appMenuTextColorSelected: res.result?.app_menu_text_color_selected,
          appMenuTextColorOnHover: res.result?.app_menu_text_color_on_hover,
        },
        {root: true},
      );
      commit('setOwner', res.result?.owner);
      await dispatch(
        'partner/actionSetPartnerIntegrationVisibility',
        res.result?.partner_integration_visibility,
        {root: true},
      );
      commit('setIsCompanyEmployee', ice.result);
      commit('setDashboardSettings', res.result);
      commit('setAnalyticsAllowed', res.result?.analytics_allowed);
      commit('setInvoicesAllowed', res.result?.invoices_allowed);
      commit('setBankConnections', res.result?.connections);
      commit('setChecksAllowed', res.result?.checks_allowed);
      commit('setIsCpa', res.result?.is_cpa);
      commit('setGSheetsLink', res.result?.gsheet);
      commit('setIsStandalone', res.result?.is_standalone);
      localStorage.setItem(FYGR_COMPANY, companyId);
      if (token) {
        localStorage.setItem(FYGR_ACCESS_TOKEN, token);
      }
      if (pretendUserToken) {
        localStorage.setItem(FYGR_PRETENDED_USER, pretendUserToken);
      }
      localStorage.setItem(FYGR_COMPANY, companyId);
      const decoded = jwtDecode(token);
      let id = decoded.identity.replace(new RegExp('^0x'), '');
      id = Buffer.from(id, 'hex');
      id = uuidParse.unparse(id).toString('utf8');
      commit('setUser', {
        id,
        ex_email: decoded.email,
        role: decoded.role,
        is_active: decoded.is_active,
        salesVisibility: decoded.salesVisibility,
        email: res.result?.email,
        companyOwner: res.result?.company_owner,
        isCompanyCpa: res.result?.is_cpa,
      });
      commit('setIsActiveForCurrentCompany', res.result?.isActive);
      // can't use mixin as it's a vuex store -> need vuex module to be working so duplicate method.
      commit('setAuthorized', true);
      const params = await callAPI('POST', 'getParams', {}, true);
      if (params && params?.result) {
        commit('setBackendParams', params?.result);
      }
      await dispatch('app/actionGetInAppMessage', {}, {root: true});
    },
    async actionInitSponsorhip() {},
    async actionPretend({commit}, {user}) {
      const res = await callAPI(
        'POST',
        'adminGetUserToken',
        {id: user.id},
        true,
      );
      const userWithToken = {...user, ...res?.result};
      commit('setPretendedUser', userWithToken);
      // Sometimes we have problems with update when changing user. here we want to force it
      window.localStorage.removeItem('FYGR_COMPANY');
      window.localStorage.setItem(FYGR_COMPANY, userWithToken.companyId);
      window.location.reload();
    },
    actionCheckLocalStorage(_state, data) {
      function getLocalStorageSizeInBytes() {
        let total = 0;
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            const value = localStorage.getItem(key);
            total += new Blob([key + value]).size;
          }
        } catch (err) {
          console.error('❗ Error calculating localStorage size:', err);
        }
        return total;
      }
      function removeLargestItemIfOver2MB() {
        try {
          const limit = 2 * 1024 * 1024; // 2 MB
          const used = getLocalStorageSizeInBytes();

          if (used <= limit) {
            return;
          }
          let largestKey = null;
          let largestSize = 0;
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            const value = localStorage.getItem(key);
            const {size} = new Blob([key + value]);

            if (size > largestSize) {
              largestSize = size;
              largestKey = key;
            }
          }
          window.analytics.track(en.EN_LOCALSTORAGE_CLEAR.name, {
            ...en.EN_LOCALSTORAGE_CLEAR.properties,
            email: data,
            largestKey,
            largestSize,
          });

          if (largestKey !== null) {
            localStorage.removeItem(largestKey);
          }
        } catch (err) {
          console.error('❗ Error accessing localStorage:', err);
        }
      }

      removeLargestItemIfOver2MB();
    },
    async actionSignIn({commit, state, dispatch}, {username, password}) {
      dispatch('actionCheckLocalStorage', {email: username});
      localStorage.removeItem(FYGR_PRETENDED_USER);
      const res = await callAPI('POST', 'signIn', {username, password});
      if (res.result?.access_token) {
        const token = jwtDecode(res.result?.access_token);
        if (token.is_active) {
          localStorage.setItem(FYGR_ACCESS_TOKEN, res.result?.access_token);
          let id = token.identity.replace(new RegExp('^0x'), '');
          id = Buffer.from(id, 'hex');
          id = uuidParse.unparse(id).toString('utf8');
          commit('setSigninPopup', false);
          commit('setUser', {
            id,
            role: token.role,
            is_active: token.is_active,
          });
          if (token.ex_id && token.email) {
            window.analytics.identify(token.ex_id, {
              email: token.email,
              pwaMode: state.pwaMode,
              isAdBlockerDetected: state.isAdBlockerDetected,
            });
          }
          dispatch('tracking/actionSendUserDeviceInfo', null, {root: true});
          return token;
        }
        return {is_active: token.is_active};
      }
      return res;
    },
    async actionGoogleSignin({dispatch, state}, req) {
      localStorage.removeItem(FYGR_PRETENDED_USER);
      localStorage.removeItem(FYGR_USER_FINISHING_SIGNUP);
      const send = req;
      if (localStorage.getItem('redirect_uri')) {
        send.redirect_uri = localStorage.getItem('redirect_uri');
      }
      const res = await callAPI('POST', 'googleSignin', send);
      if (res.result && res.result.access_token) {
        const toReturn = await dispatch(
          'user/actionInitToken',
          res.result.access_token,
          {
            root: true,
          },
        );
        localStorage.removeItem('redirect_uri');
        window.analytics.identify(res.result.access_token.ex_id, {
          email: res.result.access_token.email,
          pwaMode: state.pwaMode,
          isAdBlockerDetected: state.isAdBlockerDetected,
        });
        dispatch('tracking/actionSendUserDeviceInfo', null, {root: true});
        return toReturn;
      }
      return res;
    },
    async actionSignUp({state, dispatch}, {body, header}) {
      /* TODO: quite ugly but will do the job on the short term */
      const res = await callAPI(
        'POST',
        'signUp',
        body,
        false,
        20,
        0,
        false,
        undefined,
        header,
      );
      /* const res = await callAPI('POST', 'signUp', body); */
      if (res.result && res.result?.accessToken) {
        const token = jwtDecode(res.result.accessToken);
        if (token.ex_id && token.email) {
          window.analytics.alias(token.ex_id);
          window.analytics.identify(token.ex_id, {
            email: token.email,
            pwaMode: state.pwaMode,
            isAdBlockerDetected: state.isAdBlockerDetected,
          });
        }
        dispatch('tracking/actionSendUserDeviceInfo', null, {root: true});
      }
      return res;
    },
    async actionInitToken({commit}, accessToken) {
      const token = jwtDecode(accessToken);
      if (token.is_active) {
        localStorage.setItem(FYGR_ACCESS_TOKEN, accessToken);
        let id = token.identity.replace(new RegExp('^0x'), '');
        id = Buffer.from(id, 'hex');
        id = uuidParse.unparse(id).toString('utf8');
        commit('setSigninPopup', false);
        commit('setUser', {
          id,
          role: token.role,
          is_active: token.is_active,
        });
        if (token.ex_id && token.email) {
          window.analytics.identify(token.ex_id, {
            email: token.email,
          });
        }
        return token;
      }
      return {is_active: token.is_active};
    },
    async actionGoogleAuthSignup({dispatch}, req) {
      localStorage.removeItem(FYGR_PRETENDED_USER);
      const send = req;
      if (localStorage.getItem('redirect_uri')) {
        send.redirect_uri = localStorage.getItem('redirect_uri');
      }
      const res = await callAPI('POST', 'googleSignup', send);
      // if (res.accessToken) {
      //   const token = jwtDecode(res.accessToken);
      //   if (token.ex_id && token.email) {
      //     window.analytics.alias(token.ex_id);
      //     window.analytics.identify(token.ex_id, {
      //       email: token.email,
      //     });
      //   }
      // }
      if (res.result && res.result?.access_token) {
        await dispatch('user/actionInitToken', res.result.access_token, {
          root: true,
        });
        dispatch('tracking/actionSendUserDeviceInfo', null, {root: true});
        localStorage.removeItem('redirect_uri');
      }
      return res;
    },
    async actionGetGoogleAuthURL(_, params) {
      const res = await callAPI('POST', 'getGoogleAuthURL', params);
      return res;
    },
    async actionConfirmEmail(_, {token}) {
      const res = await callAPI('POST', 'confirmEmail', {
        emailConfirmationToken: token,
      });
      if (res.result?.access_token) {
        localStorage.setItem(FYGR_ACCESS_TOKEN, res.result.access_token);
        setTimeout(() => {
          window.location = '/dashboard';
        }, 5000);
      }
      return res.result;
    },
    async actionUpdatePassword(_, {password, token}) {
      const res = await callAPI('POST', 'recoverPasswordConfirm', {
        password,
        recoveryToken: token,
      });
      return res;
    },
    async actionRecoverPassword(_, email) {
      const res = await callAPI('POST', 'recoverPasswordRequest', {email});
      return res;
    },
    async actionLogout({commit, dispatch}) {
      stashPostLoginRedirect();
      window.location.href = '/signin';
      commit('setAuthorized', false);
      commit('unauthUser');
      await dispatch('company/actionUnauthCompany', {}, {root: true});
      await dispatch('partner/reset', {}, {root: true});
      localStorage.removeItem(FYGR_PRETENDED_USER);
      localStorage.removeItem(FYGR_COMPANY);
      localStorage.removeItem(FYGR_ACCESS_TOKEN);
      localStorage.removeItem(FYGR_USER_FINISHING_SIGNUP);
      localStorage.removeItem(FYGR_USER_SELECTED_PARTNER);
      localStorage.removeItem(FYGR_CURRENT_SELECTED_PARTNER);
      localStorage.removeItem('fintecture_customer_id');
      localStorage.removeItem('fintecture_code');
      localStorage.removeItem('fintecture_access_token');
      localStorage.removeItem('fintecture_provider');
    },
    async actionGetUserProfile({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getUserInfo', {companyId});
      if (res && res.result) {
        commit('setProfileData', res.result);
        return res.result;
      }
    },
    async actionFinishSignup({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'finishSignup', {companyId, ...params});
      if (!res.error) {
        commit('setFinishingSignup', false);
      }
      return res;
    },
    async actionGetCompanyInfo({commit}, id) {
      const res = await callAPI('POST', 'getCompanyInfo', {companyId: id});
      if (res?.result) {
        commit('setCompanyData', res.result);
      }
      return res;
    },
    async actionUpdateUserProfile(_, profile) {
      const res = await callAPI('POST', 'updateUserProfile', {profile});
      return res;
    },
    async actionVerifyProfile(_, payload) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'verifyUserProfile', {
        companyId,
        ...payload,
      });
      return res;
    },
    async actionInviteUser(_, params) {
      const res = await callAPI('POST', 'addUser', params);
      return res;
    },
    async actionUpdateCompany(_, company) {
      let toSend = company.id ? company : {company};
      toSend = {
        company_id: toSend.id,
        ...toSend,
      };
      const res = await callAPI('POST', 'updateCompany', toSend);
      return res;
    },
    async actionUpdateUserPassword(_, {password, newPassword}) {
      const res = await callAPI('POST', 'updateUserPassword', {
        password,
        newPassword,
      });
      return res;
    },
    async actionGetEmployeeList({commit}, params) {
      const res = await callAPI('POST', 'getEmployeeList', {...params});
      commit('setEmployeeList', res.result);
      return res.result;
    },
    async actionGetEmployeeCompanies(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getEmployeeCompanies', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionSetEmployeeCompanies(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setEmployeeCompanies', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionGetCompaniesList({commit}, params) {
      const res = await callAPI('POST', 'getCompaniesList', params || {});
      if (res && res.result) {
        if (params && params.returnOnly) {
          return res.result;
        }
        commit('setCompanies', res.result);
        return res.result;
      }
    },
    actionSetFinishingSignup({commit}, param) {
      commit('setFinishingSignup', param);
    },
    async actionGetGroups({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getGroups', {
        companyId: params || companyId,
      });
      if (!params) {
        commit('setGroups', res.result.groups);
      }
      return res;
    },
    async actionChangeGroups(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'setGroup', {...params, companyId});
    },
    async actionSetEmployeeActive(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'setEmployeeActive', {...params, companyId});
    },
    async actionDeleteGroup({commit}, {id, migrateTo}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteGroup', {
        groupId: id,
        migrateTo,
        companyId,
      });
      if (!res.error) {
        commit('removeGroup', id);
      }
      return res;
    },
    async actionGetPermissions({commit}, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      try {
        const res = await callAPI('POST', 'getPermissions', {
          companyId: params || companyId,
        });
        if (!params && res.result) {
          commit('setPermissions', res.result);
        }
        return res.result;
      } catch (err) {
        commit('setPermissions', {});
        console.warn(err);
      }
    },
    async actionCreateCompany({commit}, {name, siret, parentId}) {
      const companyId = window.utils.genId();
      commit('addCompany', [companyId, name]);
      const res = await callAPI('POST', 'createCompany', {
        name,
        companyId,
        siret,
        parentId,
      });
      return {id: companyId, res};
    },
    async actionGetMaxCategoryDepth({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getMaxCategoryDepth', {companyId});
      if (res?.result) {
        commit('setMaxCategoryDepth', res.result);
      }
      return res;
    },
    async actionGetMaxConnections({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getMaxConnections', {companyId});
      commit('setMaxConnections', res.result);
    },
    async actionModifyGroup({commit}, group) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'modifyGroup', {group, companyId});
      commit('modifyGroup', {...group, companyId});
    },
    async actionResendConfirmation(_, req) {
      const res = await callAPI('POST', 'resendInvitation', req || {});
      return res;
    },
    async actionGetUsersList(_, params) {
      const res = await callAPI('POST', 'getUsersList', params, true);
      if (res.result && res.result.length > 0) {
        return res.result.sort((a, b) => {
          if (a.email > b.email) {
            return 1;
          }
          if (a.email < b.email) {
            return -1;
          }
          return 0;
        });
      }
      return [];
    },
    async actionGetAllCompaniesList(_, params) {
      const res = await callAPI('POST', 'getAllCompaniesList', params, true);
      return res.result;
    },
    async actionCreateSpreadsheet(_, id) {
      const res = await callAPI('POST', 'createSpreadsheet', {id}, true, 0);
      return res;
    },
    async actionSpreasheedProtection(_, params) {
      const res = await callAPI(
        'POST',
        'spreadsheetProtection',
        params,
        true,
        0,
      );
      return res.result;
    },
    async actionChangeEmail(_, params) {
      const res = await callAPI('POST', 'changeEmail', params, true, 0);
      return res;
    },
    async actionUpdateSpreadsheet(_, id) {
      const res = await callAPI('POST', 'updateGsheet', {id}, true, 0);
      return res;
    },
    async actionAcceptInvitation(_, token) {
      const res = await callAPI('POST', 'acceptInvitation', {token});
      return res.result;
    },
    async actionDeleteEmployee(_, employeeId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'removeUser', {
        companyId,
        employeeId,
      });
      return res;
    },
    async actionSignUpByInvite(_, {token, password, name, surname, phone, ip}) {
      const res = await callAPI('POST', 'signUpByInvite', {
        token,
        password,
        name,
        surname,
        phone,
        ip,
      });
      return res.result;
    },
    async actionDeleteCompany({commit}, {id}) {
      // const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'deleteCompany',
        {companyId: id},
        true,
        0,
      );
      if (!res.error) {
        commit('removeFromCompanies', id);
      }
      return res;
    },
    async actionSetMaxCategoryDepth(_, params) {
      await callAPI('POST', 'setMaxCategoryDepth', params, true);
    },
    async actionSetMaxConnections(_, params) {
      await callAPI('POST', 'setMaxConnections', params, true);
    },
    async actionUpdateCompanySetting(_, params) {
      return await callAPI('POST', 'updateCompanySetting', params, true);
    },
    async actionSetGsheetTab(_, params) {
      await callAPI('POST', 'setGsheetTab', params, true);
    },
    async actionSetDefaultCurrency(_, params) {
      await callAPI('POST', 'setDefaultCurrency', params, true);
    },
    async actionGetDefaultCurrency({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getDefaultCurrency', {companyId});
      if (res && res.result) {
        commit('setUserCurrency', res.result.defaultCurrency);
      }
    },
    async actionChangeUserIsActive(_, params) {
      await callAPI('POST', 'changeUserIsActive', params, true);
    },
    async actionChangeCompanyIsActive(_, params) {
      await callAPI('POST', 'changeCompanyIsActive', params, true);
    },
    async actionConnectionsRequest(_, type) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      await callAPI('POST', 'connectionsRequest', {companyId, type});
    },
    async actionUpdateConfig(_, params) {
      await callAPI('POST', 'updateConfig', params, true, 0);
    },
    async actionSetBackup(_, params) {
      await callAPI('POST', 'setBackup', params, true);
    },
    async actionSetCustomPermissions(_, params) {
      await callAPI('POST', 'setCustomPermissions', params, true);
    },
    async actionSetBypass(_, params) {
      await callAPI('POST', 'setBypass', params, true);
    },
    async actionSetSalesVisibility(_, params) {
      await callAPI('POST', 'setSalesVisibility', params, true);
    },
    async actionSetInvoiceForCompany(_, params) {
      await callAPI('POST', 'setInvoiceForCompany', params, true);
    },
    async actionSetChecksForCompany(_, params) {
      await callAPI('POST', 'setChecksForCompany', params, true);
    },
    async actionGetForecastEnabled({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getForecastEnabled', {companyId});
      if (res && res.result) {
        commit('setForecastEnabled', res.result.forecastEnabled);
        commit('setForecastVersion', res.result.forecastVersion);
      }
    },
    async actionSetForecastEnabled(_, params) {
      await callAPI('POST', 'setForecastEnabled', params, true);
    },
    actionFinishingSignupUserData({commit}, params) {
      commit('setFinishingSignupUserData', params);
      localStorage.setItem(FYGR_USER_FINISHING_SIGNUP, JSON.stringify(params));
    },
    actionSetCurrentStep({commit}, params) {
      commit('setCurrentStep', params);
    },
    async actionSetAnalyticsForCompany(_, params) {
      await callAPI('POST', 'setAnalyticsForCompany', params, true);
    },
    actionSetStepperLastStep({commit}, params) {
      commit('setStepperLastStep', params);
    },
    async actionGetCompanyInfos({commit, dispatch}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getCompanyInfos', {companyId}).catch(
        (err) => {
          commit('setAuthorized', false);
          commit('unauthUser');
          localStorage.removeItem(FYGR_PRETENDED_USER);
          localStorage.removeItem(FYGR_ACCESS_TOKEN);
          localStorage.removeItem(FYGR_COMPANY);
          localStorage.removeItem(FYGR_USER_FINISHING_SIGNUP);
          localStorage.removeItem(FYGR_USER_SELECTED_PARTNER);
          throw err;
        },
      );
      await dispatch(
        'company/actionGetStyles',
        {
          headerColor: res.result?.app_header_bg_color,
          logo: res.result?.app_logo,
          icon: res.result?.app_icon,
          consentIcon: res.result?.consent_icon,
          contentKey: res.result?.content_key,
          appMenuTextColor: res.result?.app_menu_text_color,
          appMenuTextColorSelected: res.result?.app_menu_text_color_selected,
          appMenuTextColorOnHover: res.result?.app_menu_text_color_on_hover,
        },
        {root: true},
      );
      commit('setDashboardSettings', res.result);
      commit('setAnalyticsAllowed', res.result.analytics_allowed);
      return res.result;
    },
    actionSetIsCpa({commit}, params) {
      commit('setIsCpa', params);
    },
    async actionGetUserPreferences({commit, state}) {
      if (isUserLogged()) {
        let res;
        try {
          res = await callAPI('GET', 'getUserPreferences');
        } catch (error) {
          console.log('Call to getUserPreferences failed', error);
        }
        const preferences = res?.result;
        if (preferences) {
          commit('setUserPreferences', preferences);
          if (preferences.locale) {
            localStorage.setItem('LOCALE', preferences.locale);
            dayjs.locale(preferences.locale.substring(0, 2));
            await waitForWeglot();
            window.Weglot?.switchTo(preferences.locale.substring(0, 2));
            return state.preferences;
          }
        }
      }

      let locale = localStorage.getItem('LOCALE');
      if (!locale) {
        locale = await getLocaleWithWeglot();
      }

      if (locale) {
        dayjs.locale(locale.substring(0, 2));
        localStorage.setItem('LOCALE', locale);
        commit('setLocale', locale);
        if (window.Weglot?.initialized) {
          window.Weglot?.switchTo(locale.substring(0, 2));
        }
      }

      return state.preferences;
    },
    async actionSetBlurredBalancePreferences({state, commit}, params) {
      const res = await callAPI('POST', 'setBlurredBalancePreferences', params);
      if (
        res &&
        res.result &&
        typeof res.result === 'object' &&
        !Array.isArray(res.result) &&
        Object.keys(res.result).length > 0
      ) {
        const prefs = {...state.preferences, ...res?.result};
        commit('setUserPreferences', prefs);
      }
    },
    async actionSetLocale({state, commit}, params) {
      if (!params || !params.locale) {
        return;
      }

      let res = null;

      if (isUserLogged()) {
        const userId =
          state.pretendedUser && state.pretendedUser.id
            ? state.pretendedUser.id
            : state.user.id;
        res = await callAPI('POST', `user/${userId}/locale`, params);
        window.analytics.identify(
          userId,
          {email: state.user.email},
          {Intercom: {language_override: params.locale}},
        );
      }

      dayjs.locale(params.locale.substring(0, 2));
      commit('setLocale', params.locale);
      localStorage.setItem('LOCALE', params.locale);
      window.Weglot.switchTo(params.locale.substring(0, 2));

      return res;
    },
    async actionSetUserPreferences({state, dispatch}, params) {
      if (!params) {
        return;
      }
      const userId =
        state.pretendedUser && state.pretendedUser.id
          ? state.pretendedUser.id
          : state.user.id;
      const res = await callAPI(
        'POST',
        `user/${userId}/user_preferences`,
        params,
      );
      dispatch('actionGetUserPreferences');
      return res;
    },
    async actionGetOnboardingCustomization(_commit, param) {
      const res = await callAPI('GET', `onboarding_customization/${param}`);
      return res.result;
    },
    async actionGetMarketingCustomizationSignUp(_commit, key) {
      const res = await callAPI(
        'GET',
        `marketing_customization/signup_page?key=${key}`,
      );
      return res.result;
    },
    async actionDetectAdBlocker({commit}) {
      async function adBlockerDetector() {
        try {
          const adResponse = await fetch(
            'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js',
          );
          return adResponse;
        } catch (e) {
          console.warn(e);
          return null;
        }
      }

      const adBlockerDetectorResponse = await adBlockerDetector();

      commit('setAdBlockerDetected', !adBlockerDetectorResponse?.ok);
    },
  },
};
